import React, { useLayoutEffect, useRef, useState } from 'react'
import { CrossClientTabButton } from './CrossClientTabButton'
import './CrossClientLandingTabs.scss'

export type CrossClientTab = 'needs-action' | 'completed' | 'running' | 'recommended' | 'scheduled'

export interface CrossClientTabItem {
  key: CrossClientTab
  label: string
  count: number
  hideCount?: boolean
}

interface CrossClientTabBarProps {
  tabs: CrossClientTabItem[]
  activeTab: CrossClientTab
  onSelect: (key: CrossClientTab) => void
  rightSlot?: React.ReactNode
}

interface IndicatorStyle {
  left: number
  top: number
  width: number
  height: number
}

interface CrossClientTabSegmentProps {
  tabs: CrossClientTabItem[]
  activeTab: CrossClientTab
  ariaLabel: string
  onSelect: (key: CrossClientTab) => void
}

const CrossClientTabSegment: React.FC<CrossClientTabSegmentProps> = ({ tabs, activeTab, ariaLabel, onSelect }) => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [indicator, setIndicator] = useState<IndicatorStyle | null>(null)

  // Depend on activeIndex + counts (not the fresh-every-render `tabs` array) so the resize listener
  // isn't re-bound each render; counts is in the deps because button widths change with the badges.
  const activeIndex = tabs.findIndex((tab) => tab.key === activeTab)
  const counts = tabs.map((tab) => tab.count).join(',')
  useLayoutEffect(() => {
    const measure = () => {
      const el = activeIndex >= 0 ? buttonRefs.current[activeIndex] : null
      setIndicator(el ? { left: el.offsetLeft, top: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight } : null)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [activeIndex, counts])

  // Roving-tabindex keyboard nav: only the active tab is tabbable; arrows/Home/End move selection + focus.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const last = tabs.length - 1
    const current = activeIndex < 0 ? 0 : activeIndex
    let nextIndex: number
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = current >= last ? 0 : current + 1
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = current <= 0 ? last : current - 1
        break
      case 'Home':
        nextIndex = 0
        break
      case 'End':
        nextIndex = last
        break
      default:
        return
    }
    e.preventDefault()
    onSelect(tabs[nextIndex].key)
    buttonRefs.current[nextIndex]?.focus()
  }

  return (
    <div
      role='tablist'
      aria-label={ariaLabel}
      className='position-relative d-flex gap-1 align-items-center zocks-bg-color-gray-100 zocks-border-color-gray-200 border p-1 cross-client-tabs-segment'
      onKeyDown={handleKeyDown}
    >
      {indicator ? (
        <div
          className='cross-client-tabs-segment-indicator'
          style={{ transform: `translateX(${indicator.left}px)`, top: indicator.top, width: indicator.width, height: indicator.height }}
        />
      ) : null}
      {tabs.map((tab, index) => (
        <CrossClientTabButton
          key={tab.key}
          ref={(el) => {
            buttonRefs.current[index] = el
          }}
          tab={tab}
          active={activeTab === tab.key}
          // Fall back to the first button so a segment without the active tab stays keyboard-reachable.
          tabbable={activeTab === tab.key || (activeIndex < 0 && index === 0)}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

export const CrossClientTabBar: React.FC<CrossClientTabBarProps> = ({ tabs, activeTab, onSelect, rightSlot }) => {
  const mainTabs = tabs.filter((tab) => tab.key !== 'recommended')
  const recommendedTab = tabs.find((tab) => tab.key === 'recommended')

  return (
    <div className='d-flex gap-2 align-items-center justify-content-between flex-wrap'>
      <div className='d-flex gap-2 align-items-center flex-wrap'>
        <CrossClientTabSegment tabs={mainTabs} activeTab={activeTab} ariaLabel='Client query views' onSelect={onSelect} />
        {recommendedTab ? <CrossClientTabSegment tabs={[recommendedTab]} activeTab={activeTab} ariaLabel='Recommended queries' onSelect={onSelect} /> : null}
      </div>
      {rightSlot ?? null}
    </div>
  )
}
